// Phase 7 sub-bucket 17 — chip-state ↔ FilterTree bidirectional mapping for
// the admin MDF requests queue. First Saved Views consumer.
//
// The queue page has two URL-driven chip filters (`status`, `fund`) plus a
// free-text search (`q`). When a saved view is active the chips reset to
// their "all" state and the view's stored FilterTree drives the read; when
// the user picks a chip the view is cleared and a synthesized FilterTree
// flows into the URL via `?filter=<base64>`.
//
// The free-text search stays client-side and OUTSIDE the FilterTree — the
// `mdf_request` catalog has `campaign_name` (contains) but the SQL ilike on
// joined partner_name is annoying to write and would change nothing
// user-visible at sub-1k row volumes (sub-bucket 15 rationale carries
// through).

import {
  FILTER_SCHEMA_VERSION,
  type FilterNode,
  type FilterTree,
  type MdfRequestStatus,
} from "@colab/shared";

export const ALL_STATUSES_SENTINEL = "all" as const;
export const ALL_FUNDS_SENTINEL = "__all__" as const;

export type RequestQueueChips = {
  status: typeof ALL_STATUSES_SENTINEL | MdfRequestStatus;
  fundId: typeof ALL_FUNDS_SENTINEL | string;
};

const EMPTY_CHIPS: RequestQueueChips = {
  status: ALL_STATUSES_SENTINEL,
  fundId: ALL_FUNDS_SENTINEL,
};

/**
 * Translate the current chip state into a {@link FilterTree}, or `null`
 * when no chip is active (both chips at their "all" sentinel).
 *
 * Used when the user adds a chip while there's no saved view active:
 * synthesize a tree, serialize as base64 JSON into `?filter=`, and the
 * worker translates it back via `applyFilterToQuery`. Mutually exclusive
 * with the saved-view branch — the page never carries both chips AND a
 * `?view=` at the same time.
 */
export function chipsToFilterTree(
  chips: RequestQueueChips,
): FilterTree | null {
  const conditions: FilterNode[] = [];
  if (chips.status !== ALL_STATUSES_SENTINEL) {
    conditions.push({
      kind: "condition",
      version: FILTER_SCHEMA_VERSION,
      field: "status",
      operator: "equals",
      value: chips.status,
    });
  }
  if (chips.fundId !== ALL_FUNDS_SENTINEL) {
    conditions.push({
      kind: "condition",
      version: FILTER_SCHEMA_VERSION,
      field: "fund_id",
      operator: "equals",
      value: chips.fundId,
    });
  }
  if (conditions.length === 0) return null;
  return {
    kind: "group",
    version: FILTER_SCHEMA_VERSION,
    combinator: "AND",
    children: conditions,
  };
}

/**
 * Reverse direction — read a stored saved-view filter back into chip state
 * IF the tree is the kind of shape the chips can express (top-level AND
 * group of one or two equals conditions on `status` / `fund_id`). Returns
 * `null` when the tree doesn't round-trip — the chip toolbar then stays at
 * its "all" sentinel and the FilterTree drives the read silently.
 *
 * Today's chip toolbar is intentionally NOT shown when a saved view is
 * active (the view drives the read; the user edits filters via
 * AddFilterMenu) so this helper exists primarily for future "advance the
 * chip UI from a view" affordances and for documentation.
 */
export function filterTreeToChips(
  tree: FilterTree | null,
): RequestQueueChips | null {
  if (!tree || tree.kind !== "group" || tree.combinator !== "AND") return null;
  if (tree.children.length === 0) return EMPTY_CHIPS;
  if (tree.children.length > 2) return null;

  const next: RequestQueueChips = { ...EMPTY_CHIPS };
  for (const child of tree.children) {
    if (child.kind !== "condition") return null;
    if (child.operator !== "equals" || typeof child.value !== "string")
      return null;
    if (child.field === "status") {
      next.status = child.value as MdfRequestStatus;
    } else if (child.field === "fund_id") {
      next.fundId = child.value;
    } else {
      return null;
    }
  }
  return next;
}

/**
 * Stable hash of a FilterTree for use as part of a TanStack query key
 * tuple. Two filters that produce identical JSON serialise to the same
 * key; small enough to keep inline without an FNV1a helper.
 */
export function hashFilterTree(tree: FilterTree | null): string {
  if (!tree) return "none";
  return JSON.stringify(tree);
}

/**
 * Base64-encode a FilterTree for transport in the `?filter=` query param.
 * The worker decodes via `atob` + `parseFilterFromSearchParam`. Encoding
 * the JSON first avoids URI-component reserved-character escaping at the
 * shell + curl boundary.
 */
export function encodeFilterTreeParam(tree: FilterTree | null): string | null {
  if (!tree) return null;
  return btoa(JSON.stringify(tree));
}
